import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { provideRouter, withPreloading, PreloadAllModules, withRouterConfig, TitleStrategy } from '@angular/router';
import { WakiTitleStrategy } from '@core/strategies/page-title.strategy';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';

import { routes } from './app.routes';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { studentFeature } from '@features/school-management/store/student.store';
import { environment } from '@environments/environment';
import { WhitelabelService } from '@core/services/whitelabel.service';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { Directionality } from '@angular/cdk/bidi';
import { LanguageDirectionality } from '@core/i18n/language-directionality';
import { provideFieldErrorTranslations } from '@shared/validators/form-validator';

export const appConfig: ApplicationConfig = {
  providers: [
    // Router — PreloadAllModules loads lazy chunks in the background after app init,
    // eliminating the "first-click delay" after login.
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withRouterConfig({ canceledNavigationResolution: 'computed' })
    ),


    // Page title strategy — sets document.title based on route (or derived from URL)
    { provide: TitleStrategy, useClass: WakiTitleStrategy },

    // HTTP Client with interceptors
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),

    // FE-236: resolve a whitelabelled business's vocabulary and logo BEFORE the
    // first paint. Without this the shell renders WAKI's own wording and then
    // visibly re-renders into the client's. It fails open on a timeout, so a
    // slow config call costs a moment, never the portal.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const whitelabel = inject(WhitelabelService);
        return () => whitelabel.ready();
      },
    },

    // Animations for Angular Material
    provideAnimations(),

    // Use Material Symbols Outlined globally
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-symbols-outlined' } },

    // Mirror everything the CDK renders in an overlay (dialogs, select panels,
    // menus) with the portal language — the stock Directionality reads <html dir>
    // once at startup and never updates.
    { provide: Directionality, useClass: LanguageDirectionality },

    // getFieldError() is a plain function called from templates; this hands it
    // TranslationService so inline field errors follow the portal language.
    provideFieldErrorTranslations(),

    // NgRx Store
    provideStore({
      [studentFeature.name]: studentFeature.reducer
    }),

    // NgRx Effects
    provideEffects([]),

    // NgRx DevTools (only in development)
    ...(!environment.production ? [
      provideStoreDevtools({
        maxAge: 25,
        logOnly: environment.production,
        autoPause: true,
        trace: false,
        traceLimit: 75
      })
    ] : []),

    // Service Worker (PWA)
    ...(environment.features.isPwaEnabled ? [
      provideServiceWorker('ngsw-worker.js', {
        enabled: environment.production,
        registrationStrategy: 'registerWhenStable:30000'
      })
    ] : [])
  ]
};
